/**
 * 上行立体声 s16le PCM 电平遥测（MQTT 发出前 / Opus 编码前）。
 * 生产构建请设 REACT_APP_UPLINK_PCM_LEVEL_LOG=1，并依赖 craco 保留 console.warn。
 */

export type UplinkStereoS16Stats = {
  /** 总样本数（L+R 各算一个） */
  sampleCount: number;
  /** 绝对值峰值 0..32767 */
  peakAbs: number;
  /** 全样本 RMS（与 peak 同量纲，约 0..23170） */
  rmsInt: number;
  /** peak / 32767 */
  peakNorm: number;
  /** rmsInt / 32767 */
  rmsNorm: number;
};

export function computeStereoS16lePeakRms(ab: ArrayBuffer): UplinkStereoS16Stats | null {
  if (ab.byteLength < 4 || ab.byteLength % 2 !== 0) {
    return null;
  }
  const view = new DataView(ab);
  const n = ab.byteLength / 2;
  let peak = 0;
  let sumSq = 0;
  for (let i = 0; i < n; i++) {
    const s = view.getInt16(i * 2, true);
    const a = Math.abs(s);
    if (a > peak) {
      peak = a;
    }
    sumSq += s * s;
  }
  const rmsInt = Math.sqrt(sumSq / n);
  return {
    sampleCount: n,
    peakAbs: peak,
    rmsInt: Math.round(rmsInt),
    peakNorm: peak / 32767,
    rmsNorm: rmsInt / 32767,
  };
}

export function shouldLogUplinkPcmLevelTelemetry(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  const v = process.env.REACT_APP_UPLINK_PCM_LEVEL_LOG?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

type TelemetryContext = 'pcm_mqtt' | 'opus_pre_encode';

let telemetryPacketSeq = 0;
let telemetryLastLogMs = 0;

/**
 * 节流：前 3 包必打；之后每 25 包或间隔 ≥4s 打一条，避免 PTT 时刷屏。
 */
export function logUplinkStereoS16TelemetryThrottled(
  deviceId: string,
  context: TelemetryContext,
  pcmS16leStereo: ArrayBuffer
): void {
  if (!shouldLogUplinkPcmLevelTelemetry()) {
    return;
  }
  const stats = computeStereoS16lePeakRms(pcmS16leStereo);
  if (!stats) {
    console.warn('[WaveLink 上行 PCM 电平] 无法计算（buffer 长度或对齐异常）', {
      deviceId,
      context,
      byteLength: pcmS16leStereo.byteLength,
    });
    return;
  }
  telemetryPacketSeq += 1;
  const now = Date.now();
  const shouldEmit =
    telemetryPacketSeq <= 3 ||
    telemetryPacketSeq % 25 === 0 ||
    now - telemetryLastLogMs >= 4000;
  if (!shouldEmit) {
    return;
  }
  telemetryLastLogMs = now;
  console.warn('[WaveLink 上行 PCM 电平]', {
    deviceId,
    context,
    seq: telemetryPacketSeq,
    int16Peak: stats.peakAbs,
    int16Rms: stats.rmsInt,
    peakNorm: Number(stats.peakNorm.toFixed(4)),
    rmsNorm: Number(stats.rmsNorm.toFixed(4)),
    stereoSamples: stats.sampleCount,
    pcmBytes: pcmS16leStereo.byteLength,
  });
}
